<script lang="ts">
  import { api, ApiError } from '../../lib/api';
  import { user } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import { withPageTitle } from '../../lib/page-title';
  import Button from '../../components/ui/Button.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Banner from '../../components/ui/Banner.svelte';

  $effect(() => withPageTitle('Verify your email'));

  // Holding page after email/password signup. Shows "check your inbox"
  // with a resend button (server throttles at 3/hr — we surface that as
  // a toast on 429). User can keep playing in the 7-day grace window;
  // the soft-lockout middleware enforces the cutoff server-side.

  let resending = $state(false);

  async function resend(): Promise<void> {
    if (resending) return;
    resending = true;
    try {
      await api('/api/auth/email/verify/resend', { method: 'POST' });
      showToast('Verification email sent. Check your inbox.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        showToast('Too many requests. Wait a bit and try again.', 'warn');
      } else {
        showToast("Couldn't send the email. Please try again.", 'error');
      }
    } finally {
      resending = false;
    }
  }
</script>

<Card padding="lg" elevated>
  <div class="auth-form">
    <header class="auth-form__header">
      <h2 class="auth-form__title">Check your email</h2>
      <p class="auth-form__subtitle">
        {#if $user?.email}
          We sent a verification link to <strong>{$user.email}</strong>.
        {:else}
          We sent a verification link to your inbox.
        {/if}
      </p>
    </header>

    <Banner variant="info">
      You can keep playing for 7 days while you verify. After that, you'll
      need a verified email to sign back in.
    </Banner>

    <div class="auth-form__actions">
      <Button variant="primary" loading={resending} onclick={resend}>
        Resend verification email
      </Button>
      <Button variant="ghost" href="/">Back to game</Button>
    </div>

    <p class="auth-form__alt">
      Wrong email? <a href="/account">Update it in account settings</a>.
    </p>
  </div>
</Card>
